<template>
  <div class="multi-route-tracker">
    <!-- Header -->
    <div class="tracker-header d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-2">
        <div class="header-icon">
          <i class="bi bi-truck"></i>
        </div>
        <div>
          <h4 class="mb-0">Fleet Weather Planner</h4>
          <small class="text-muted">Multi-route temperature &amp; weather monitoring</small>
        </div>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <div class="btn-group btn-group-sm">
          <button 
            class="btn" 
            :class="unitGroup === 'metric' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setUnitGroup('metric')"
          >
            °C / km
          </button>
          <button 
            class="btn" 
            :class="unitGroup === 'us' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setUnitGroup('us')"
          >
            °F / mi
          </button>
        </div>
      </div>
    </div>

    <!-- Sign-in Required -->
    <div v-if="!hasSession" class="alert alert-warning d-flex align-items-center">
      <i class="bi bi-lock me-3 fs-4"></i>
      <div class="flex-grow-1">
        <strong>Sign in required</strong>
        <p class="mb-0 small">Please sign in to access weather data for route planning.</p>
      </div>
      <a class="btn btn-primary btn-sm" href="#" onclick="window.dialogManager?.showSignIn(); return false;">
        <i class="bi bi-box-arrow-in-right me-1"></i>Sign In
      </a>
    </div>

    <template v-else>
      <div class="row g-4">
        <!-- Left Column: Route Management -->
        <div class="col-lg-5">
          <!-- Routes List -->
          <div class="card mb-3">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <i class="bi bi-signpost-2 me-2"></i>Routes
                <span class="badge bg-secondary ms-1">{{ routes.length }}</span>
              </h6>
              <button class="btn btn-sm btn-success" @click="addRoute">
                <i class="bi bi-plus-lg me-1"></i>Add Route
              </button>
            </div>
            <div class="card-body p-2">
              <div v-if="routes.length === 0" class="text-center text-muted py-4">
                <i class="bi bi-signpost d-block fs-3 mb-2"></i>
                <span>No routes defined</span>
                <button class="btn btn-link btn-sm d-block mx-auto" @click="addRoute">
                  Add your first route
                </button>
              </div>

              <!-- Simple Route List -->
              <div class="route-list">
                <div 
                  v-for="(route, routeIndex) in routes" 
                  :key="route.id"
                  class="route-list-item d-flex align-items-center p-2 mb-2 rounded border"
                  :class="{
                    'border-success': getRouteStatus(route) === 'ok',
                    'border-warning': getRouteStatus(route) === 'warning',
                    'border-danger': getRouteStatus(route) === 'danger'
                  }"
                >
                  <span 
                    class="route-badge me-2"
                    :style="{ backgroundColor: route.color }"
                  >{{ routeIndex + 1 }}</span>
                  <div class="flex-grow-1">
                    <div class="fw-medium">{{ route.name || `Route ${routeIndex + 1}` }}</div>
                    <small class="text-muted">
                      {{ route.stops.length }} stop(s)
                      <span v-if="route.totalDistance">· {{ formatDistance(route.totalDistance) }}</span>
                    </small>
                  </div>
                  <!-- Weather Score -->
                  <span 
                    v-if="getRouteResults(route).length > 0"
                    class="badge me-2"
                    :class="{
                      'bg-success': getClearWeatherPercent(route) === 100,
                      'bg-warning text-dark': getClearWeatherPercent(route) >= 50 && getClearWeatherPercent(route) < 100,
                      'bg-danger': getClearWeatherPercent(route) < 50
                    }"
                    :title="`${getClearWeatherPercent(route)}% of stops have clear weather`"
                  >
                    {{ getClearWeatherPercent(route) }}% Clear
                  </span>
                  <!-- Better alternative available -->
                  <button 
                    v-if="route.hasBetterAlternative"
                    class="btn btn-sm btn-success me-2"
                    @click.stop="applyAlternativeRoute(route, route.bestAlternativeIndex)"
                    :title="`Switch to alternative with ${100 - route.bestAlternativeBadPercent}% clear weather`"
                  >
                    <i class="bi bi-arrow-repeat me-1"></i>{{ 100 - route.bestAlternativeBadPercent }}%
                  </button>
                  <!-- View details -->
                  <button 
                    v-if="getRouteResults(route).length > 0"
                    class="btn btn-sm btn-outline-secondary me-1" 
                    @click="showRouteDetails(route)"
                    title="View route details"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-primary" @click="editRoute(route)">
                    <i class="bi bi-pencil"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Temperature Thresholds -->
          <div class="card mb-3">
            <div class="card-header py-2">
              <h6 class="mb-0">
                <i class="bi bi-thermometer-half me-2"></i>Temperature Limits
              </h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-6">
                  <label class="form-label small">
                    <i class="bi bi-thermometer-snow text-info me-1"></i>Cold Limit
                  </label>
                  <div class="input-group input-group-sm">
                    <input 
                      type="number" 
                      class="form-control"
                      v-model.number="coldThreshold"
                      @change="updateCriteria"
                    >
                    <span class="input-group-text">{{ tempUnit }}</span>
                  </div>
                </div>
                <div class="col-6">
                  <label class="form-label small">
                    <i class="bi bi-thermometer-sun text-danger me-1"></i>Heat Limit
                  </label>
                  <div class="input-group input-group-sm">
                    <input 
                      type="number" 
                      class="form-control"
                      v-model.number="heatThreshold"
                      @change="updateCriteria"
                    >
                    <span class="input-group-text">{{ tempUnit }}</span>
                  </div>
                </div>
              </div>

              <!-- Weather Alerts Toggle -->
              <div class="form-check form-switch mt-3">
                <input 
                  type="checkbox" 
                  class="form-check-input" 
                  id="weatherAlerts"
                  v-model="includeWeatherAlerts"
                  @change="updateCriteria"
                >
                <label class="form-check-label small" for="weatherAlerts">
                  Include severe weather alerts
                </label>
              </div>
            </div>
          </div>

          <!-- Date Range & Run -->
          <div class="card mb-3">
            <div class="card-body">
              <label class="form-label small">Shipping Window</label>
              <div class="btn-group btn-group-sm w-100 mb-3">
                <button 
                  v-for="period in datePeriods"
                  :key="period.value"
                  class="btn"
                  :class="startPeriod === period.value ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange(period.value)"
                >
                  {{ period.label }}
                </button>
              </div>

              <button 
                class="btn btn-success w-100"
                :disabled="!canRunAllRoutes || isRunning || checkingAlternatives"
                @click="runAllRoutes"
              >
                <span v-if="isRunning">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  Checking {{ completedCount }}/{{ totalStopsCount }} stops...
                </span>
                <span v-else-if="checkingAlternatives">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  Finding better routes...
                </span>
                <span v-else>
                  <i class="bi bi-play-fill me-1"></i>
                  Check All Routes ({{ totalStopsCount }} stops)
                </span>
              </button>
              
              <!-- Alternatives status -->
              <div v-if="checkingAlternatives" class="alert alert-info py-2 px-3 mt-2 mb-0 small">
                <i class="bi bi-search me-1"></i>
                Searching for better routes to avoid weather issues...
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Map & Results -->
        <div class="col-lg-7">
          <!-- Map Container -->
          <div class="card mb-3">
            <div class="card-header py-2">
              <h6 class="mb-0"><i class="bi bi-map me-2"></i>Route Map</h6>
            </div>
            <div class="card-body p-0 map-wrapper" style="height: 300px; position: relative;">
              <!-- Placeholder shown before map is ready -->
              <div v-if="!mapReady" class="map-placeholder d-flex align-items-center justify-content-center bg-light" style="position: absolute; inset: 0; z-index: 1;">
                <div class="text-center text-muted">
                  <i class="bi bi-map fs-1 d-block mb-2"></i>
                  <span>Loading map...</span>
                </div>
              </div>
              <!-- Map container - Leaflet will render here -->
              <div 
                ref="mapContainer" 
                class="route-map" 
                style="height: 100%; width: 100%;"
              ></div>
            </div>
          </div>

          <!-- Fleet Summary -->
          <div v-if="hasResults" class="fleet-summary mb-4">
            <div class="row g-3">
              <div class="col-4">
                <div class="summary-card text-center p-3 border rounded">
                  <div class="fs-2 fw-bold text-success">{{ safeRoutesCount }}</div>
                  <div class="small text-muted">Routes Clear</div>
                </div>
              </div>
              <div class="col-4">
                <div class="summary-card text-center p-3 border rounded">
                  <div class="fs-2 fw-bold text-warning">{{ cautionRoutesCount }}</div>
                  <div class="small text-muted">Caution</div>
                </div>
              </div>
              <div class="col-4">
                <div class="summary-card text-center p-3 border rounded">
                  <div class="fs-2 fw-bold text-danger">{{ dangerRoutesCount }}</div>
                  <div class="small text-muted">Don't Ship</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Route Results - Simple Cards -->
          <div v-if="hasResults" class="route-results">
            <div class="row g-2">
              <div 
                v-for="(route, routeIndex) in routes" 
                :key="route.id"
                class="col-6 col-md-4"
              >
                <div 
                  class="route-card p-3 rounded border h-100"
                  :class="{
                    'border-success bg-success bg-opacity-10': getRouteStatus(route) === 'ok',
                    'border-warning bg-warning bg-opacity-10': getRouteStatus(route) === 'warning',
                    'border-danger bg-danger bg-opacity-10': getRouteStatus(route) === 'danger'
                  }"
                  @click="showRouteDetails(route)"
                  role="button"
                >
                  <div class="d-flex align-items-center mb-2">
                    <span 
                      class="route-badge me-2"
                      :style="{ backgroundColor: route.color }"
                    >{{ routeIndex + 1 }}</span>
                    <span 
                      class="badge ms-auto"
                      :class="{
                        'bg-success': getRouteStatus(route) === 'ok',
                        'bg-warning text-dark': getRouteStatus(route) === 'warning',
                        'bg-danger': getRouteStatus(route) === 'danger'
                      }"
                    >
                      {{ getRouteStatusLabel(route) }}
                    </span>
                  </div>
                  <h6 class="mb-1 text-truncate">{{ route.name || `Route ${routeIndex + 1}` }}</h6>
                  <small class="text-muted d-block">
                    {{ route.stops.length }} stop(s)
                    <span v-if="route.totalDistance">· {{ formatDistance(route.totalDistance) }}</span>
                  </small>
                  <small class="text-muted d-block" v-if="route.departureTime">
                    <i class="bi bi-clock me-1"></i>{{ route.departureTime }}
                    <span class="ms-2"><i class="bi bi-speedometer me-1"></i>{{ route.avgSpeed }} {{ unitGroup === 'us' ? 'mph' : 'km/h' }}</span>
                  </small>
                  <div class="mt-2 text-center">
                    <div class="mb-1">
                      <span 
                        class="badge fs-6"
                        :class="{
                          'bg-success': getClearWeatherPercent(route) === 100,
                          'bg-warning text-dark': getClearWeatherPercent(route) >= 50 && getClearWeatherPercent(route) < 100,
                          'bg-danger': getClearWeatherPercent(route) < 50
                        }"
                      >
                        {{ getClearWeatherPercent(route) }}% Clear
                      </span>
                    </div>
                    <span class="fs-5 fw-bold">{{ getRouteMaxTemp(route) }}{{ tempUnit }}</span>
                    <small class="text-muted d-block">max temp</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!hasResults" class="empty-state text-center py-5">
            <i class="bi bi-truck fs-1 text-muted mb-3 d-block"></i>
            <h5 class="text-muted">Plan Your Fleet Routes</h5>
            <p class="text-muted mb-0">
              Add routes with origin, destination, and waypoints, then check weather conditions along each route.
            </p>
          </div>
        </div>
      </div>

      <!-- Route Details Modal -->
      <div v-if="selectedRoute" class="route-modal-overlay" @click.self="closeDetailsModal">
        <div class="route-modal route-modal-wide">
          <div class="route-modal-header d-flex align-items-center p-3 border-bottom"
            :class="{
              'bg-success bg-opacity-10': previewingAlternative === null && selectedRouteClearPercent === 100,
              'bg-warning bg-opacity-10': previewingAlternative === null && selectedRouteClearPercent >= 50 && selectedRouteClearPercent < 100,
              'bg-danger bg-opacity-10': previewingAlternative === null && selectedRouteClearPercent < 50
            }"
          >
            <span 
              class="route-badge me-2"
              :style="{ backgroundColor: selectedRoute.color }"
            >{{ routes.indexOf(selectedRoute) + 1 }}</span>
            <div class="flex-grow-1">
              <h5 class="mb-0">{{ selectedRoute.name || `Route ${routes.indexOf(selectedRoute) + 1}` }}</h5>
              <small class="text-muted">
                Viewing: {{ previewInfo?.label || 'Primary Route' }}
                <span v-if="previewInfo?.distance">· {{ formatDistance(previewInfo.distance) }}</span>
              </small>
            </div>
            <span 
              class="badge me-3 fs-6"
              :class="{
                'bg-success': (previewInfo?.badWeatherPercent || 0) === 0,
                'bg-warning text-dark': (previewInfo?.badWeatherPercent || 0) > 0 && (previewInfo?.badWeatherPercent || 0) <= 50,
                'bg-danger': (previewInfo?.badWeatherPercent || 0) > 50
              }"
            >
              {{ 100 - (previewInfo?.badWeatherPercent || 0) }}% Clear
            </span>
            <button class="btn btn-sm btn-outline-secondary" @click="closeDetailsModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          
          <div class="route-modal-body p-3">
            <!-- Route Options Selector (Primary + Alternatives) -->
            <div v-if="selectedRouteAlternatives.length > 0" class="route-options-bar mb-3">
              <label class="form-label small text-muted mb-2">
                <i class="bi bi-signpost-2 me-1"></i>Compare Route Options (click to preview)
              </label>
              <div class="btn-group btn-group-sm w-100" role="group">
                <!-- Primary Route -->
                <button 
                  type="button"
                  class="btn position-relative"
                  :class="previewingAlternative === null ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="previewAlternative(null)"
                >
                  <i class="bi bi-geo-alt me-1"></i>
                  Primary
                  <span class="badge ms-1" :class="selectedRouteClearPercent === 100 ? 'bg-success' : selectedRouteClearPercent >= 80 ? 'bg-warning text-dark' : 'bg-danger'">
                    {{ selectedRouteClearPercent }}%
                  </span>
                </button>
                <!-- Alternative Routes -->
                <button 
                  v-for="alt in selectedRouteAlternatives"
                  :key="alt.alternativeIndex"
                  type="button"
                  class="btn position-relative"
                  :class="[
                    previewingAlternative === alt.alternativeIndex ? 'btn-primary' : 'btn-outline-secondary',
                    { 'route-recommended': alt.badWeatherPercent === 0 && selectedRouteBadPercent > 0 }
                  ]"
                  @click="previewAlternative(alt.alternativeIndex)"
                >
                  <span v-if="alt.badWeatherPercent === 0 && selectedRouteBadPercent > 0" class="recommended-star">★</span>
                  Alt {{ alt.alternativeIndex }}
                  <span class="badge ms-1" :class="alt.badWeatherPercent === 0 ? 'bg-success' : alt.badWeatherPercent <= 20 ? 'bg-warning text-dark' : 'bg-danger'">
                    {{ 100 - alt.badWeatherPercent }}%
                  </span>
                </button>
              </div>
              <small v-if="selectedRouteBestAlt" class="text-success d-block mt-1">
                <i class="bi bi-star-fill me-1"></i>Alternative {{ selectedRouteBestAlt.alternativeIndex }} has clearer weather
              </small>
            </div>

            <!-- Temperature Timeline (sampled for performance) -->
            <div class="temp-timeline mb-3">
              <div class="d-flex gap-1">
                <div 
                  v-for="result in timelineResults" 
                  :key="result.id"
                  class="temp-stop flex-grow-1"
                  :class="'severity-' + result.maxSeverity"
                  :title="`${result.name || result.location} - ${getArrivalTemp(result)}${tempUnit}`"
                >
                  <div class="stop-marker"></div>
                  <div class="stop-temp">
                    {{ getArrivalTemp(result) }}°
                  </div>
                  <div class="stop-time text-muted" v-if="result.arrivalInfo">
                    {{ result.arrivalInfo.arrivalTimeStr }}
                  </div>
                  <div class="stop-label text-truncate">
                    {{ getStopLabel(result) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Arrival Schedule Table -->
            <div class="table-responsive">
              <table class="table table-sm table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Stop</th>
                    <th class="text-center">Arrival</th>
                    <th class="text-center">Distance</th>
                    <th class="text-center">Temp @Arrival</th>
                    <th class="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="result in tableResults" 
                    :key="result.id"
                    :class="'table-' + (result.maxSeverity === 'danger' ? 'danger' : result.maxSeverity === 'warning' ? 'warning' : '')"
                  >
                    <td class="text-nowrap">
                      <small>{{ getStopLabel(result) }}</small>
                    </td>
                    <td class="text-center">
                      <small>
                        <template v-if="result.arrivalInfo">
                          <span v-if="result.arrivalInfo.arrivalDays > 0" class="text-muted">
                            +{{ result.arrivalInfo.arrivalDays }}d
                          </span>
                          {{ result.arrivalInfo.arrivalTimeStr }}
                        </template>
                        <template v-else>-</template>
                      </small>
                    </td>
                    <td class="text-center">
                      <small v-if="result.arrivalInfo">
                        {{ formatDistance(result.arrivalInfo.distanceKm) }}
                      </small>
                      <small v-else>-</small>
                    </td>
                    <td class="text-center">
                      <small class="fw-bold">{{ getArrivalTemp(result) }}{{ tempUnit }}</small>
                    </td>
                    <td class="text-center">
                      <span 
                        class="badge"
                        :class="{
                          'bg-success': result.maxSeverity === 'ok',
                          'bg-warning text-dark': result.maxSeverity === 'warning',
                          'bg-danger': result.maxSeverity === 'danger'
                        }"
                      >
                        {{ result.maxSeverity === 'ok' ? 'OK' : result.maxSeverity === 'warning' ? 'Caution' : 'Alert' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
                <!-- Show more button -->
                <tfoot v-if="hiddenRowCount > 0 && !showAllTableRows">
                  <tr>
                    <td colspan="5" class="text-center py-2">
                      <button 
                        class="btn btn-sm btn-link" 
                        @click="showAllTableRows = true"
                      >
                        Show {{ hiddenRowCount }} more stops...
                      </button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          
          <!-- Modal Footer with Apply Button -->
          <div v-if="previewingAlternative !== null" class="route-modal-footer p-3 border-top bg-light">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <strong>{{ previewInfo?.label }}</strong>
                <span class="text-muted ms-2">{{ formatDistance(previewInfo?.distance) }}</span>
              </div>
              <button 
                class="btn btn-success"
                @click="applyPreviewedAlternative"
              >
                <i class="bi bi-check-lg me-1"></i>Use This Route
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Route Edit Modal -->
      <div v-if="editingRoute" class="route-modal-overlay" @click.self="closeEditModal">
        <div class="route-modal">
          <div class="route-modal-header d-flex align-items-center p-3 border-bottom bg-light">
            <span 
              class="route-badge me-2"
              :style="{ backgroundColor: editingRoute.color }"
            >{{ routes.indexOf(editingRoute) + 1 }}</span>
            <div class="flex-grow-1">
              <h5 class="mb-0">Edit Route</h5>
            </div>
            <button class="btn btn-sm btn-outline-secondary" @click="closeEditModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          
          <div class="route-modal-body p-3">
            <!-- Route Name -->
            <div class="mb-3">
              <label class="form-label small">Route Name</label>
              <input 
                type="text" 
                class="form-control form-control-sm"
                v-model="editingRoute.name"
                placeholder="e.g., East Coast Run"
              >
            </div>

            <!-- Route Mode -->
            <div class="mb-3">
              <label class="form-label small">Route Type</label>
              <div class="btn-group btn-group-sm w-100">
                <button 
                  class="btn"
                  :class="editingRoute.mode === 'manual' ? 'btn-secondary' : 'btn-outline-secondary'"
                  @click="editingRoute.mode = 'manual'"
                >
                  <i class="bi bi-pencil me-1"></i>Manual Stops
                </button>
                <button 
                  class="btn"
                  :class="editingRoute.mode === 'auto' ? 'btn-secondary' : 'btn-outline-secondary'"
                  @click="editingRoute.mode = 'auto'"
                >
                  <i class="bi bi-magic me-1"></i>Auto-Generate
                </button>
              </div>
            </div>

            <!-- Manual Mode: Stop List -->
            <div v-if="editingRoute.mode === 'manual'" class="stops-section">
              <label class="form-label small d-flex justify-content-between">
                <span>Stops / Checkpoints</span>
                <button 
                  class="btn btn-link btn-sm p-0"
                  @click="addStop(editingRoute)"
                >
                  <i class="bi bi-plus"></i> Add
                </button>
              </label>
              
              <div class="stop-list" style="max-height: 200px; overflow-y: auto;">
                <div 
                  v-for="(stop, stopIndex) in editingRoute.stops" 
                  :key="stop.id"
                  class="stop-item d-flex align-items-center gap-2 mb-2"
                >
                  <div class="stop-type-icon">
                    <i :class="getStopIcon(stop.type)"></i>
                  </div>
                  <div class="flex-grow-1">
                    <input 
                      type="text" 
                      class="form-control form-control-sm"
                      v-model="stop.location"
                      :placeholder="getStopPlaceholder(stop.type)"
                    >
                  </div>
                  <select 
                    class="form-select form-select-sm" 
                    style="width: auto;"
                    v-model="stop.type"
                  >
                    <option value="origin">Origin</option>
                    <option value="waypoint">Waypoint</option>
                    <option value="destination">Destination</option>
                  </select>
                  <button 
                    class="btn btn-sm btn-outline-danger"
                    @click="removeStop(editingRoute, stopIndex)"
                    :disabled="editingRoute.stops.length <= 2"
                  >
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Auto Mode: Origin/Destination + Interval -->
            <div v-if="editingRoute.mode === 'auto'" class="auto-route-section">
              <div class="mb-2">
                <label class="form-label small">
                  <i class="bi bi-geo-alt text-success me-1"></i>Origin
                </label>
                <input 
                  type="text" 
                  class="form-control form-control-sm"
                  v-model="editingRoute.origin"
                  placeholder="City, address, or coordinates"
                >
              </div>
              <div class="mb-2">
                <label class="form-label small">
                  <i class="bi bi-geo-alt-fill text-danger me-1"></i>Destination
                </label>
                <input 
                  type="text" 
                  class="form-control form-control-sm"
                  v-model="editingRoute.destination"
                  placeholder="City, address, or coordinates"
                >
              </div>
              <div class="mb-2">
                <label class="form-label small">Weather Check Interval</label>
                <select class="form-select form-select-sm" v-model.number="editingRoute.intervalKm">
                  <option :value="50">Every {{ unitGroup === 'us' ? '30 miles' : '50 km' }}</option>
                  <option :value="80">Every {{ unitGroup === 'us' ? '50 miles' : '80 km' }}</option>
                  <option :value="160">Every {{ unitGroup === 'us' ? '100 miles' : '160 km' }}</option>
                  <option :value="320">Every {{ unitGroup === 'us' ? '200 miles' : '320 km' }}</option>
                </select>
              </div>
              <button 
                class="btn btn-sm btn-outline-primary w-100"
                :disabled="!editingRoute.origin || !editingRoute.destination || editingRoute.isGenerating"
                @click="generateRouteStops(editingRoute)"
              >
                <span v-if="editingRoute.isGenerating">
                  <span class="spinner-border spinner-border-sm me-1"></span>
                  Generating...
                </span>
                <span v-else>
                  <i class="bi bi-lightning me-1"></i>Generate Checkpoints
                </span>
              </button>
              
              <!-- Generated stops preview -->
              <div v-if="editingRoute.stops.length > 0" class="mt-2">
                <small class="text-success">
                  <i class="bi bi-check-circle me-1"></i>{{ editingRoute.stops.length }} checkpoints generated
                </small>
              </div>
              <div v-else-if="editingRoute.origin && editingRoute.destination" class="mt-2">
                <small class="text-warning">
                  <i class="bi bi-exclamation-triangle me-1"></i>Click Generate to create checkpoints
                </small>
              </div>
            </div>

            <!-- Departure Settings (per-route) -->
            <div class="departure-settings mt-3 pt-3 border-top">
              <label class="form-label small fw-bold">
                <i class="bi bi-clock me-1"></i>Departure Settings
              </label>
              <div class="row g-2">
                <div class="col-6">
                  <label class="form-label small text-muted">Departure Time</label>
                  <input 
                    type="time" 
                    class="form-control form-control-sm"
                    v-model="editingRoute.departureTime"
                  >
                </div>
                <div class="col-6">
                  <label class="form-label small text-muted">Avg Speed</label>
                  <div class="input-group input-group-sm">
                    <input 
                      type="number" 
                      class="form-control"
                      v-model.number="editingRoute.avgSpeed"
                      min="20"
                      max="200"
                    >
                    <span class="input-group-text">{{ unitGroup === 'us' ? 'mph' : 'km/h' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="route-modal-footer d-flex gap-2 p-3 border-top bg-light">
            <button 
              class="btn btn-sm btn-outline-danger"
              @click="removeRouteFromModal()"
            >
              <i class="bi bi-trash me-1"></i>Delete
            </button>
            <button 
              class="btn btn-sm btn-outline-secondary"
              @click="duplicateRoute(editingRoute); closeEditModal();"
              :disabled="!canCloseEditModal"
            >
              <i class="bi bi-copy me-1"></i>Duplicate
            </button>
            <button 
              class="btn btn-sm btn-primary ms-auto"
              @click="closeEditModal"
              :disabled="!canCloseEditModal"
              :title="!canCloseEditModal ? 'Generate checkpoints first' : ''"
            >
              <i class="bi bi-check me-1"></i>Done
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useWeatherExceptionEngine } from '../useWeatherExceptionEngine.js';
import { createCriterion } from '../exceptionCriteriaPresets.js';
import { 
  generateRouteWeatherPoints,
  generateAlternativeRoutes,
  formatDistance as formatDist,
  formatDuration 
} from '../routeService.js';

// Route colors for visual distinction
const ROUTE_COLORS = [
  '#0d6efd', // blue
  '#6610f2', // purple
  '#d63384', // pink
  '#fd7e14', // orange
  '#20c997', // teal
  '#6c757d', // gray
];

export default {
  name: 'MultiRouteShippingTracker',

  props: {
    /** Optional override for the API server root URL */
    serverRoot: {
      type: String,
      default: null
    }
  },


  setup(props) {
    const engine = useWeatherExceptionEngine({
      storageKey: 'multi-route-shipping',
      defaultStartPeriod: 'next5days',
      defaultUnitGroup: 'us',
      maxConcurrentRequests: 5,
      serverRoot: props.serverRoot
    });

    onMounted(() => {
      engine.initSession();
    });

    return { ...engine };
  },

  data() {
    return {
      // Routes
      routes: [],
      activeRouteId: null,
      expandedRoutes: [],
      routeColorIndex: 0,

      // Thresholds
      coldThreshold: 32,
      heatThreshold: 95,
      includeWeatherAlerts: true,

      // Date periods
      datePeriods: [
        { value: 'next3days', label: '3 Days' },
        { value: 'next5days', label: '5 Days' },
        { value: 'next7days', label: '7 Days' }
      ],

      // Results tracking
      routeResults: {}, // { routeId: [results] }
      routeAlternatives: {}, // { routeId: [{alternativeIndex, weatherPoints, routePath, results}] }
      selectedRoute: null, // Route selected for detail modal
      previewingAlternative: null, // Alternative being previewed in detail modal (null = primary)
      editingRoute: null, // Route being edited in modal
      checkingAlternatives: false, // True while fetching alternatives
      showAllTableRows: false, // Toggle to show all rows in table vs initial batch
      
      // Map
      map: null,
      mapReady: false,
      routeLayers: {}
    };
  },

  computed: {
    tempUnit() {
      return this.unitGroup === 'us' ? '°F' : '°C';
    },

    totalStopsCount() {
      return this.routes.reduce((sum, r) => sum + r.stops.length, 0);
    },

    canRunAllRoutes() {
      return this.routes.length > 0 && 
             this.routes.some(r => r.stops.length >= 1 && r.stops.some(s => s.location));
    },

    hasResults() {
      return Object.keys(this.routeResults).length > 0;
    },

    safeRoutesCount() {
      return this.routes.filter(r => this.getRouteStatus(r) === 'ok').length;
    },

    cautionRoutesCount() {
      return this.routes.filter(r => this.getRouteStatus(r) === 'warning').length;
    },

    dangerRoutesCount() {
      return this.routes.filter(r => this.getRouteStatus(r) === 'danger').length;
    },

    visibleDates() {
      // Get dates from any route's results
      for (const routeId in this.routeResults) {
        const results = this.routeResults[routeId];
        if (results?.[0]?.days) {
          return results[0].days.map(d => d.datetime).slice(0, 5);
        }
      }
      return [];
    },

    canCloseEditModal() {
      if (!this.editingRoute) return true;
      // In auto mode, must have generated checkpoints
      if (this.editingRoute.mode === 'auto') {
        return this.editingRoute.stops.length > 0;
      }
      // In manual mode, must have at least 2 stops with locations
      return this.editingRoute.stops.filter(s => s.location).length >= 2;
    },

    // === Modal computed properties (cached for performance) ===
    
    /** Cached alternatives for selected route */
    selectedRouteAlternatives() {
      if (!this.selectedRoute) return [];
      return this.routeAlternatives[this.selectedRoute.id] || [];
    },

    /** Cached bad weather percent for selected route */
    selectedRouteBadPercent() {
      if (!this.selectedRoute) return 0;
      const results = this.routeResults[this.selectedRoute.id] || [];
      if (results.length === 0) return 0;
      const badStops = results.filter(r => 
        r.maxSeverity === 'warning' || r.maxSeverity === 'danger'
      ).length;
      return Math.round((badStops / results.length) * 100);
    },

    /** Cached clear weather percent for selected route */
    selectedRouteClearPercent() {
      return 100 - this.selectedRouteBadPercent;
    },

    /** Cached best alternative for selected route */
    selectedRouteBestAlt() {
      if (this.selectedRouteAlternatives.length === 0) return null;
      const betterAlts = this.selectedRouteAlternatives
        .filter(a => a.badWeatherPercent < this.selectedRouteBadPercent)
        .sort((a, b) => a.badWeatherPercent - b.badWeatherPercent);
      return betterAlts.length > 0 ? betterAlts[0] : null;
    },

    /** Cached results for the currently previewed route option */
    previewResults() {
      if (!this.selectedRoute) return [];
      if (this.previewingAlternative === null) {
        return this.routeResults[this.selectedRoute.id] || [];
      }
      const alt = this.selectedRouteAlternatives.find(a => a.alternativeIndex === this.previewingAlternative);
      return alt ? alt.results : [];
    },

    /** 
     * Sampled timeline results - show max 15 stops in the visual timeline 
     * to prevent overcrowding and improve performance
     */
    timelineResults() {
      const results = this.previewResults;
      if (results.length <= 15) return results;
      
      // Always include first and last, sample evenly from the middle
      const sampled = [results[0]];
      const step = (results.length - 2) / 13; // 13 middle spots
      for (let i = 1; i < 14; i++) {
        const idx = Math.round(1 + (i - 1) * step);
        if (idx < results.length - 1) {
          sampled.push(results[idx]);
        }
      }
      sampled.push(results[results.length - 1]);
      return sampled;
    },

    /** Cached info for the currently previewed route option */
    previewInfo() {
      if (!this.selectedRoute) return null;
      if (this.previewingAlternative === null) {
        return {
          label: 'Primary Route',
          distance: this.selectedRoute.totalDistance,
          badWeatherPercent: this.selectedRouteBadPercent
        };
      }
      const alt = this.selectedRouteAlternatives.find(a => a.alternativeIndex === this.previewingAlternative);
      if (!alt) return null;
      return {
        label: `Alternative #${alt.alternativeIndex}`,
        distance: alt.totalDistance,
        badWeatherPercent: alt.badWeatherPercent
      };
    },

    /** Table results - show first 10 initially for fast render, then all on demand */
    tableResults() {
      const results = this.previewResults;
      if (this.showAllTableRows || results.length <= 10) {
        return results;
      }
      return results.slice(0, 10);
    },

    /** Number of hidden rows in table */
    hiddenRowCount() {
      return Math.max(0, this.previewResults.length - 10);
    }
  },

  watch: {
    unitGroup(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.convertThresholds(oldVal, newVal);
        this.convertSpeed(oldVal, newVal);
      }
    }
  },

  mounted() {
    this.initializeThresholds();
    this.updateCriteria();
    this.loadRoutes();
    this.initMap();
  },

  beforeUnmount() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },

  methods: {
    // Speed unit conversion for all routes
    convertSpeed(fromUnit, toUnit) {
      for (const route of this.routes) {
        if (route.avgSpeed) {
          if (fromUnit === 'metric' && toUnit === 'us') {
            // km/h to mph
            route.avgSpeed = Math.ceil((route.avgSpeed / 1.60934) / 5) * 5;
          } else if (fromUnit === 'us' && toUnit === 'metric') {
            // mph to km/h
            route.avgSpeed = Math.ceil((route.avgSpeed * 1.60934) / 5) * 5;
          }
        }
      }
      this.saveRoutes();
    },

    // Map Management
    async initMap() {
      await nextTick();
      
      // Wait for Leaflet to be available (check window.L for global)
      const leaflet = typeof L !== 'undefined' ? L : (typeof window !== 'undefined' ? window.L : undefined);
      if (!leaflet) {
        // Retry after a short delay (max 10 attempts)
        if (!this._mapRetries) this._mapRetries = 0;
        if (this._mapRetries < 10) {
          this._mapRetries++;
          setTimeout(() => this.initMap(), 200);
        }
        return;
      }
      
      if (!this.$refs.mapContainer) {
        setTimeout(() => this.initMap(), 100);
        return;
      }
      
      try {
        const container = this.$refs.mapContainer;
        
        this.map = leaflet.map(container, {
          center: [39.8, -98.5],
          zoom: 4,
          zoomControl: true
        });
      
        leaflet.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap contributors'
        }).addTo(this.map);
        
        this.mapReady = true;
        
        // Force map to recalculate size after becoming visible
        await nextTick();
        this.map.invalidateSize();
      } catch (err) {
        console.error('Map initialization failed:', err);
      }
    },

    updateMapRoutes() {
      if (!this.map) return;
      
      const leaflet = typeof L !== 'undefined' ? L : window.L;
      if (!leaflet) return;
      
      // Clear existing layers
      Object.values(this.routeLayers).forEach(layer => {
        if (layer.line) this.map.removeLayer(layer.line);
        if (layer.markers) layer.markers.forEach(m => this.map.removeLayer(m));
        if (layer.altLines) layer.altLines.forEach(l => this.map.removeLayer(l));
      });
      this.routeLayers = {};

      const allBounds = [];

      for (const route of this.routes) {
        const results = this.getRouteResults(route);
        if (!results.length) continue;

        // Use full route path if available, otherwise fall back to checkpoint coordinates
        let linePoints;
        if (route.routePath && route.routePath.length > 0) {
          // Use the full road geometry from routing API
          linePoints = route.routePath.map(p => [p.lat, p.lng]);
        } else {
          // Fall back to connecting checkpoint locations
          linePoints = results
            .filter(r => r.resolvedLocation?.latitude && r.resolvedLocation?.longitude)
            .map(r => [r.resolvedLocation.latitude, r.resolvedLocation.longitude]);
        }

        if (linePoints.length === 0) continue;

        // Draw route line following actual road
        const line = leaflet.polyline(linePoints, { 
          color: route.color, 
          weight: 4, 
          opacity: 0.8 
        }).addTo(this.map);

        // Add markers for each weather checkpoint
        const markers = results.map((r, idx) => {
          if (!r.resolvedLocation?.latitude) return null;
          
          const color = r.maxSeverity === 'danger' ? '#dc3545' : 
                       r.maxSeverity === 'warning' ? '#ffc107' : '#198754';
          
          const icon = leaflet.divIcon({
            className: 'route-marker',
            html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:8px;font-weight:bold;">${idx === 0 ? 'S' : idx === results.length - 1 ? 'E' : ''}</div>`,
            iconSize: [14, 14],
            iconAnchor: [7, 7]
          });

          const arrivalTemp = this.getArrivalTemp(r);
          const arrivalTime = r.arrivalInfo?.arrivalTimeStr || '';
          const lat = r.resolvedLocation.latitude.toFixed(2);
          const lon = r.resolvedLocation.longitude.toFixed(2);
          return leaflet.marker([r.resolvedLocation.latitude, r.resolvedLocation.longitude], { icon })
            .bindPopup(`<strong>${lat}, ${lon}</strong><br>Arrival: ${arrivalTime}<br>Temp: ${arrivalTemp}${this.tempUnit}`)
            .addTo(this.map);
        }).filter(Boolean);

        this.routeLayers[route.id] = { line, markers };
        allBounds.push(...linePoints);

        // Draw alternative routes as dashed lines if available
        const alternatives = this.getRouteAlternatives(route);
        if (alternatives.length > 0) {
          const altLines = [];
          for (const alt of alternatives) {
            if (alt.routePath && alt.routePath.length > 0) {
              // Check if this alternative is currently being previewed
              const isPreviewed = this.selectedRoute?.id === route.id && 
                                 this.previewingAlternative === alt.alternativeIndex;
              
              const altColor = alt.badWeatherPercent === 0 ? '#198754' : 
                              alt.badWeatherPercent <= 50 ? '#ffc107' : '#adb5bd';
              const altLine = leaflet.polyline(
                alt.routePath.map(p => [p.lat, p.lng]),
                { 
                  color: isPreviewed ? '#0d6efd' : altColor, 
                  weight: isPreviewed ? 5 : 3, 
                  opacity: isPreviewed ? 0.9 : 0.6,
                  dashArray: isPreviewed ? null : '10, 8'
                }
              ).addTo(this.map);
              
              // Add popup with alternative info
              const distLabel = this.formatDistance(alt.totalDistance);
              const clearPercent = 100 - alt.badWeatherPercent;
              altLine.bindPopup(`<strong>Alternative #${alt.alternativeIndex}</strong><br>Distance: ${distLabel}<br>Clear Weather: ${clearPercent}%<br><em>Click to view details</em>`);
              
              altLines.push(altLine);
            }
          }
          this.routeLayers[route.id].altLines = altLines;
        }
      }

      if (allBounds.length > 0) {
        this.map.fitBounds(allBounds, { padding: [40, 40] });
      }
    },

    /**
     * Lightweight update - just restyle alternative lines without redrawing everything
     * Used when switching between alternatives in the modal
     */
    updateAlternativeStyles() {
      if (!this.map || !this.selectedRoute) return;
      
      const leaflet = typeof L !== 'undefined' ? L : window.L;
      if (!leaflet) return;

      const layer = this.routeLayers[this.selectedRoute.id];
      if (!layer?.altLines) return;

      const alternatives = this.getRouteAlternatives(this.selectedRoute);
      
      // Update each alternative line's style
      layer.altLines.forEach((altLine, idx) => {
        const alt = alternatives[idx];
        if (!alt) return;
        
        const isPreviewed = this.previewingAlternative === alt.alternativeIndex;
        const altColor = alt.badWeatherPercent === 0 ? '#198754' : 
                        alt.badWeatherPercent <= 50 ? '#ffc107' : '#adb5bd';
        
        altLine.setStyle({
          color: isPreviewed ? '#0d6efd' : altColor,
          weight: isPreviewed ? 5 : 3,
          opacity: isPreviewed ? 0.9 : 0.6,
          dashArray: isPreviewed ? null : '10, 8'
        });
        
        // Bring previewed line to front
        if (isPreviewed) {
          altLine.bringToFront();
        }
      });
    },

    // Route Management
    loadRoutes() {
      const saved = localStorage.getItem('multi-route-shipping-routes');
      if (saved) {
        try {
          this.routes = JSON.parse(saved);
          // Reset transient state that shouldn't persist
          this.routes.forEach(route => {
            route.isGenerating = false;
          });
          if (this.routes.length > 0) {
            this.activeRouteId = this.routes[0].id;
          }
        } catch (e) {
          console.warn('Failed to load saved routes:', e);
        }
      }
    },

    saveRoutes() {
      localStorage.setItem('multi-route-shipping-routes', JSON.stringify(this.routes));
    },

    addRoute() {
      const color = ROUTE_COLORS[this.routeColorIndex % ROUTE_COLORS.length];
      this.routeColorIndex++;

      const newRoute = {
        id: `route-${Date.now()}`,
        name: '',
        color,
        mode: 'auto', // Default to auto-generate for easier setup
        origin: '',
        destination: '',
        intervalKm: 80,
        departureTime: '08:00', // Per-route departure time
        avgSpeed: this.unitGroup === 'us' ? 50 : 80, // Per-route speed in current unit
        stops: [],
        totalDistance: null,
        routePath: null,
        isGenerating: false
      };

      this.routes.push(newRoute);
      this.editingRoute = newRoute; // Open edit modal for new route
      this.saveRoutes();
    },

    removeRoute(index) {
      const routeId = this.routes[index].id;
      this.routes.splice(index, 1);
      delete this.routeResults[routeId];
      
      if (this.activeRouteId === routeId && this.routes.length > 0) {
        this.activeRouteId = this.routes[0].id;
      }
      this.saveRoutes();
    },

    duplicateRoute(route) {
      const color = ROUTE_COLORS[this.routeColorIndex % ROUTE_COLORS.length];
      this.routeColorIndex++;

      const duplicate = {
        ...JSON.parse(JSON.stringify(route)),
        id: `route-${Date.now()}`,
        name: route.name ? `${route.name} (copy)` : '',
        color
      };

      // Regenerate stop IDs
      duplicate.stops = duplicate.stops.map((s, i) => ({
        ...s,
        id: `stop-${Date.now()}-${i}`
      }));

      this.routes.push(duplicate);
      this.activeRouteId = duplicate.id;
      this.saveRoutes();
    },

    toggleRoute(routeId) {
      this.activeRouteId = this.activeRouteId === routeId ? null : routeId;
    },

    showRouteDetails(route) {
      // Just show the modal - no map update needed for initial open
      this.selectedRoute = route;
      this.previewingAlternative = null;
      this.showAllTableRows = false; // Reset to show limited rows initially
    },

    closeDetailsModal() {
      this.selectedRoute = null;
      this.previewingAlternative = null;
      this.showAllTableRows = false;
      // Just reset alternative styles - no full redraw needed
      // The styles will be reset on next full updateMapRoutes call
    },

    /**
     * Apply the currently previewed alternative route
     * Swaps the alternative with primary, keeping both available
     */
    applyPreviewedAlternative() {
      if (this.selectedRoute && this.previewingAlternative !== null) {
        this.swapWithAlternative(this.selectedRoute, this.previewingAlternative);
        // Switch back to viewing the new primary (which is now the applied alternative)
        this.previewingAlternative = null;
        this.showAllTableRows = false;
      }
    },

    editRoute(route) {
      this.editingRoute = route;
    },

    closeEditModal() {
      this.editingRoute = null;
      this.saveRoutes();
    },

    removeRouteFromModal() {
      if (this.editingRoute) {
        const index = this.routes.indexOf(this.editingRoute);
        if (index >= 0) {
          this.routes.splice(index, 1);
          delete this.routeResults[this.editingRoute.id];
        }
        this.editingRoute = null;
        this.saveRoutes();
      }
    },

    getRouteMaxTemp(route) {
      const results = this.getRouteResults(route);
      if (!results.length) return '--';
      const allTemps = results.map(r => this.getArrivalTemp(r)).filter(t => t !== '--');
      if (!allTemps.length) return '--';
      const min = Math.min(...allTemps);
      const max = Math.max(...allTemps);
      if (min === max) return max;
      return `${min}-${max}`;
    },

    toggleRouteDetails(routeId) {
      const index = this.expandedRoutes.indexOf(routeId);
      if (index >= 0) {
        this.expandedRoutes.splice(index, 1);
      } else {
        this.expandedRoutes.push(routeId);
      }
    },

    // Stop Management
    addStop(route) {
      route.stops.push({
        id: `stop-${Date.now()}`,
        location: '',
        type: 'waypoint'
      });
      this.saveRoutes();
    },

    removeStop(route, index) {
      if (route.stops.length > 2) {
        route.stops.splice(index, 1);
        this.saveRoutes();
      }
    },

    getStopIcon(type) {
      switch (type) {
        case 'origin': return 'bi bi-geo-alt text-success';
        case 'destination': return 'bi bi-geo-alt-fill text-danger';
        default: return 'bi bi-circle text-secondary';
      }
    },

    getStopPlaceholder(type) {
      switch (type) {
        case 'origin': return 'Origin city or address';
        case 'destination': return 'Destination city or address';
        default: return 'Waypoint city or address';
      }
    },

    // Auto-route generation
    async generateRouteStops(route) {
      if (!route.origin || !route.destination) return;

      const apiKey = this.apiKey || window.VITE_VC_API_KEY || '';
      if (!apiKey) {
        alert('API key required for route generation');
        return;
      }

      route.isGenerating = true;

      try {
        const result = await generateRouteWeatherPoints(
          route.origin,
          route.destination,
          apiKey,
          { 
            intervalKm: route.intervalKm, 
            maxPoints: 50,
            serverRoot: this.serverRoot,
            returnFullPath: true  // Get full route geometry for map
          }
        );

        const points = result.weatherPoints;
        
        route.stops = points.map(p => ({
          id: p.id,
          location: p.location,
          name: p.name,
          type: p.type,
          distanceKm: p.distanceKm
        }));

        // Store full route path for map drawing
        route.routePath = result.routePath;
        route.totalDistance = result.totalDistance;
        route.totalDuration = result.totalDuration;
        
        // Auto-generate route name from origin/destination if not set
        if (!route.name) {
          const originName = this.extractCityName(route.origin);
          const destName = this.extractCityName(route.destination);
          route.name = `${originName} → ${destName}`;
        }
      } catch (error) {
        console.error('Route generation failed:', error);
        alert(`Route generation failed: ${error.message}`);
      } finally {
        route.isGenerating = false;
        this.saveRoutes();
      }
    },

    // Thresholds
    initializeThresholds() {
      if (this.unitGroup === 'metric') {
        this.coldThreshold = 0;
        this.heatThreshold = 35;
      } else {
        this.coldThreshold = 32;
        this.heatThreshold = 95;
      }
    },

    /**
     * Extract city name from an address or coordinate string
     */
    extractCityName(addressOrCoord) {
      if (!addressOrCoord) return 'Unknown';
      
      // If it looks like coordinates, return abbreviated version
      if (/^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(addressOrCoord.trim())) {
        const parts = addressOrCoord.split(',').map(p => parseFloat(p.trim()).toFixed(1));
        return `${parts[0]},${parts[1]}`;
      }
      
      // For addresses, take the first part (usually city name)
      const parts = addressOrCoord.split(',');
      return parts[0].trim();
    },

    convertThresholds(fromUnit, toUnit) {
      const convert = (val) => {
        if (fromUnit === 'metric' && toUnit === 'us') {
          return Math.round(val * 9/5 + 32);
        } else if (fromUnit === 'us' && toUnit === 'metric') {
          return Math.round((val - 32) * 5/9);
        }
        return val;
      };

      this.coldThreshold = convert(this.coldThreshold);
      this.heatThreshold = convert(this.heatThreshold);
      this.updateCriteria();
    },

    updateCriteria() {
      const criteria = [];

      // Use hourly temp for route-based arrival time checking
      // We use 'hours' section to ensure API returns hourly data
      criteria.push(createCriterion(
        'temp', 'lt', this.coldThreshold, 'hours',
        'Too Cold',
        { color: 'danger' }
      ));

      criteria.push(createCriterion(
        'temp', 'gt', this.heatThreshold, 'hours',
        'Too Hot',
        { color: 'danger' }
      ));

      if (this.includeWeatherAlerts) {
        criteria.push(createCriterion(
          'severerisk', 'gt', 30, 'days',
          'Severe Weather Risk',
          { color: 'warning' }
        ));
      }

      this.setCriteria(criteria, 'OR');
    },

    // Run weather check for all routes
    async runAllRoutes() {
      // Collect all stops from all routes with arrival times
      const allLocations = [];
      const routeStopMap = {}; // Map location ID to route ID
      const arrivalTimeMap = {}; // Map location ID to arrival info

      for (const route of this.routes) {
        // Use per-route departure settings, with fallbacks
        const departureTime = route.departureTime || '08:00';
        const [depHour, depMin] = departureTime.split(':').map(Number);
        const departureMinutes = depHour * 60 + depMin;
        
        // Use per-route speed, with fallback
        const routeSpeed = route.avgSpeed || (this.unitGroup === 'us' ? 50 : 80);
        const speedKmh = this.unitGroup === 'us' 
          ? routeSpeed * 1.60934 
          : routeSpeed;

        let cumulativeDistanceKm = 0;
        
        for (let i = 0; i < route.stops.length; i++) {
          const stop = route.stops[i];
          if (!stop.location) continue;
          
          // Get distance (use stored distanceKm or estimate from index)
          const distanceKm = stop.distanceKm || (i * (route.intervalKm || 80));
          cumulativeDistanceKm = distanceKm;
          
          // Calculate travel time in minutes
          const travelMinutes = (cumulativeDistanceKm / speedKmh) * 60;
          const arrivalMinutes = departureMinutes + travelMinutes;
          
          // Calculate arrival hour (0-23) and overflow days
          const arrivalDays = Math.floor(arrivalMinutes / (24 * 60));
          const arrivalHour = Math.floor((arrivalMinutes % (24 * 60)) / 60);
          const arrivalMin = Math.floor(arrivalMinutes % 60);
          
          const locationEntry = {
            id: `${route.id}::${stop.id}`,
            location: stop.location,
            name: stop.name || stop.location
          };
          
          allLocations.push(locationEntry);
          routeStopMap[locationEntry.id] = route.id;
          arrivalTimeMap[locationEntry.id] = {
            distanceKm: cumulativeDistanceKm,
            travelMinutes: Math.round(travelMinutes),
            arrivalDays,
            arrivalHour,
            arrivalMin,
            arrivalTimeStr: `${String(arrivalHour).padStart(2,'0')}:${String(arrivalMin).padStart(2,'0')}`
          };
        }
      }

      if (allLocations.length === 0) return;

      // Set locations and run
      this.setLocations(allLocations);
      await this.runExceptionCheck();

      // Organize results by route and add arrival time data
      this.routeResults = {};
      for (const result of this.locationResults) {
        const routeId = routeStopMap[result.id];
        if (routeId) {
          if (!this.routeResults[routeId]) {
            this.routeResults[routeId] = [];
          }
          // Attach arrival time info to result
          result.arrivalInfo = arrivalTimeMap[result.id];
          
          // Recalculate severity based on arrival hour temperature
          this.recalculateArrivalSeverity(result);
          
          this.routeResults[routeId].push(result);
        }
      }

      // Expand all routes with results
      this.expandedRoutes = this.routes.map(r => r.id);
      
      // Update map with route results
      this.updateMapRoutes();
      
      // Check for alternatives on routes with bad weather
      await this.checkAlternativesForBadRoutes();
    },

    /**
     * Check for alternative routes on routes that have any weather issues
     */
    async checkAlternativesForBadRoutes() {
      const apiKey = this.apiKey || window.VITE_VC_API_KEY || '';
      if (!apiKey) return;

      // Find routes with ANY bad weather (badWeatherPercent > 0) that are in auto mode
      const badRoutes = this.routes.filter(r => {
        const badPercent = this.getBadWeatherPercent(r);
        return badPercent > 0 && r.mode === 'auto' && r.origin && r.destination;
      });

      if (badRoutes.length === 0) return;

      this.checkingAlternatives = true;
      this.routeAlternatives = {};

      try {
        for (const route of badRoutes) {
          await this.findBetterAlternative(route, apiKey);
        }
      } finally {
        this.checkingAlternatives = false;
        this.updateMapRoutes();
      }
    },

    /**
     * Find and check weather on alternative routes
     */
    async findBetterAlternative(route, apiKey) {
      try {
        // Generate alternative routes
        const alternatives = await generateAlternativeRoutes(
          route.origin,
          route.destination,
          apiKey,
          {
            intervalKm: route.intervalKm || 80,
            maxPoints: 50,
            serverRoot: this.serverRoot,
            numAlternatives: 3
          }
        );

        // Skip the primary route (index 0), check weather on alternatives
        const altRoutes = alternatives.filter(a => a.alternativeIndex > 0);
        if (altRoutes.length === 0) return;

        // Check weather on each alternative
        const altWithWeather = [];
        
        for (const alt of altRoutes) {
          // Build arrival time map for this alternative
          const departureTime = route.departureTime || '08:00';
          const [depHour, depMin] = departureTime.split(':').map(Number);
          const departureMinutes = depHour * 60 + depMin;
          const routeSpeed = route.avgSpeed || (this.unitGroup === 'us' ? 50 : 80);
          const speedKmh = this.unitGroup === 'us' ? routeSpeed * 1.60934 : routeSpeed;

          // Create location entries for weather check
          const altLocations = [];
          const altArrivalMap = {};
          
          for (let i = 0; i < alt.weatherPoints.length; i++) {
            const point = alt.weatherPoints[i];
            const distanceKm = point.distanceKm || 0;
            const travelMinutes = (distanceKm / speedKmh) * 60;
            const arrivalMinutes = departureMinutes + travelMinutes;
            const arrivalDays = Math.floor(arrivalMinutes / (24 * 60));
            const arrivalHour = Math.floor((arrivalMinutes % (24 * 60)) / 60);
            const arrivalMin = Math.floor(arrivalMinutes % 60);
            
            const locId = `alt${alt.alternativeIndex}-${route.id}::${point.id}`;
            altLocations.push({
              id: locId,
              location: point.location,
              name: point.name
            });
            altArrivalMap[locId] = {
              distanceKm,
              travelMinutes: Math.round(travelMinutes),
              arrivalDays,
              arrivalHour,
              arrivalMin,
              arrivalTimeStr: `${String(arrivalHour).padStart(2,'0')}:${String(arrivalMin).padStart(2,'0')}`
            };
          }

          // Run weather check on this alternative
          this.setLocations(altLocations);
          await this.runExceptionCheck();

          // Process results
          const altResults = [];
          let maxSeverity = 'ok';
          let badStops = 0;
          
          for (const result of this.locationResults) {
            result.arrivalInfo = altArrivalMap[result.id];
            this.recalculateArrivalSeverity(result);
            altResults.push(result);
            
            if (result.maxSeverity === 'danger' || result.maxSeverity === 'warning') {
              badStops++;
            }
            if (result.maxSeverity === 'danger') maxSeverity = 'danger';
            else if (result.maxSeverity === 'warning' && maxSeverity !== 'danger') maxSeverity = 'warning';
          }

          // Calculate bad weather percentage
          const badWeatherPercent = altResults.length > 0 
            ? Math.round((badStops / altResults.length) * 100) 
            : 0;

          altWithWeather.push({
            alternativeIndex: alt.alternativeIndex,
            routePath: alt.routePath,
            weatherPoints: alt.weatherPoints,
            totalDistance: alt.totalDistance,
            totalDuration: alt.totalDuration,
            distanceMiles: alt.distanceMiles,
            results: altResults,
            maxSeverity,
            badWeatherPercent
          });
        }

        // Sort by badWeatherPercent (lowest first), then by distance
        altWithWeather.sort((a, b) => {
          const pctDiff = a.badWeatherPercent - b.badWeatherPercent;
          if (pctDiff !== 0) return pctDiff;
          return a.totalDistance - b.totalDistance;
        });

        // Store alternatives for this route
        this.routeAlternatives[route.id] = altWithWeather;

        // If the best alternative has lower badWeatherPercent, highlight it
        if (altWithWeather.length > 0) {
          const primaryBadPercent = this.getBadWeatherPercent(route);
          const bestAltBadPercent = altWithWeather[0].badWeatherPercent;
          
          if (bestAltBadPercent < primaryBadPercent) {
            route.hasBetterAlternative = true;
            route.bestAlternativeIndex = altWithWeather[0].alternativeIndex;
            route.bestAlternativeBadPercent = bestAltBadPercent;
          }
        }

      } catch (error) {
        console.error('Alternative route check failed for', route.name, error);
      }
    },

    /**
     * Apply an alternative route - replace primary route with selected alternative
     * Used by the quick-apply button in the route list (closes and applies)
     */
    applyAlternativeRoute(route, altIndex) {
      const alternatives = this.routeAlternatives[route.id];
      if (!alternatives) return;
      
      const alt = alternatives.find(a => a.alternativeIndex === altIndex);
      if (!alt) return;

      // Update route with alternative data
      route.routePath = alt.routePath;
      route.totalDistance = alt.totalDistance;
      route.totalDuration = alt.totalDuration;
      route.stops = alt.weatherPoints.map(p => ({
        id: p.id,
        location: p.location,
        name: p.name,
        type: p.type,
        distanceKm: p.distanceKm
      }));

      // Update results
      this.routeResults[route.id] = alt.results;
      
      // Clear alternatives (already applied)
      delete this.routeAlternatives[route.id];
      route.hasBetterAlternative = false;
      route.appliedAlternative = altIndex;

      this.saveRoutes();
      this.updateMapRoutes();
    },

    /**
     * Swap primary route with an alternative - keeps both available
     * The old primary becomes an alternative, the selected alternative becomes primary
     */
    swapWithAlternative(route, altIndex) {
      const alternatives = this.routeAlternatives[route.id];
      if (!alternatives) return;
      
      const altIdx = alternatives.findIndex(a => a.alternativeIndex === altIndex);
      if (altIdx === -1) return;
      
      const alt = alternatives[altIdx];

      // Save current primary as a new alternative
      const oldPrimary = {
        alternativeIndex: altIndex, // Reuse the index of the one we're replacing
        routePath: route.routePath ? [...route.routePath] : [],
        weatherPoints: route.stops.map(s => ({ ...s })),
        totalDistance: route.totalDistance,
        totalDuration: route.totalDuration,
        distanceMiles: route.distanceMiles,
        results: [...(this.routeResults[route.id] || [])],
        maxSeverity: this.getRouteStatus(route),
        badWeatherPercent: this.getBadWeatherPercent(route)
      };

      // Update route with alternative data
      route.routePath = alt.routePath;
      route.totalDistance = alt.totalDistance;
      route.totalDuration = alt.totalDuration;
      route.stops = alt.weatherPoints.map(p => ({
        id: p.id,
        location: p.location,
        name: p.name,
        type: p.type,
        distanceKm: p.distanceKm
      }));

      // Update results for the new primary
      this.routeResults[route.id] = alt.results;

      // Replace the alternative with the old primary
      this.routeAlternatives[route.id][altIdx] = oldPrimary;

      // Recalculate best alternative flag
      const newBadPercent = this.getBadWeatherPercent(route);
      const betterAlt = this.routeAlternatives[route.id].find(a => a.badWeatherPercent < newBadPercent);
      route.hasBetterAlternative = !!betterAlt;
      route.bestAlternativeIndex = betterAlt?.alternativeIndex;
      route.bestAlternativeBadPercent = betterAlt?.badWeatherPercent;

      this.saveRoutes();
      this.updateMapRoutes();
    },

    /**
     * Get alternatives for a route
     */
    getRouteAlternatives(route) {
      return this.routeAlternatives[route.id] || [];
    },

    /**
     * Get the best alternative (lowest bad weather percent, better than primary)
     */
    getBestAlternative(route) {
      const alternatives = this.getRouteAlternatives(route);
      if (alternatives.length === 0) return null;
      
      const primaryBadPercent = this.getBadWeatherPercent(route);
      
      // Find alternatives that are better than primary, sorted by badWeatherPercent
      const betterAlts = alternatives
        .filter(a => a.badWeatherPercent < primaryBadPercent)
        .sort((a, b) => a.badWeatherPercent - b.badWeatherPercent);
      
      return betterAlts.length > 0 ? betterAlts[0] : null;
    },

    /**
     * Calculate percentage of stops with bad weather (warning or danger)
     */
    getBadWeatherPercent(route) {
      const results = this.getRouteResults(route);
      if (results.length === 0) return 0;
      
      const badStops = results.filter(r => 
        r.maxSeverity === 'warning' || r.maxSeverity === 'danger'
      ).length;
      
      return Math.round((badStops / results.length) * 100);
    },

    /**
     * Get the "clear weather" percentage (inverse of bad weather)
     */
    getClearWeatherPercent(route) {
      return 100 - this.getBadWeatherPercent(route);
    },

    /**
     * Preview an alternative route in the details modal
     */
    previewAlternative(altIndex) {
      this.previewingAlternative = altIndex;
      this.showAllTableRows = false; // Reset table view when switching routes
      // Use lightweight style update instead of full redraw
      this.updateAlternativeStyles();
    },

    /**
     * Get results for the currently viewed route option (primary or alternative)
     */
    getPreviewResults() {
      if (!this.selectedRoute) return [];
      
      if (this.previewingAlternative === null) {
        return this.getRouteResults(this.selectedRoute);
      }
      
      const alternatives = this.getRouteAlternatives(this.selectedRoute);
      const alt = alternatives.find(a => a.alternativeIndex === this.previewingAlternative);
      return alt ? alt.results : [];
    },

    /**
     * Get the currently previewed route option info
     */
    getPreviewInfo() {
      if (!this.selectedRoute) return null;
      
      if (this.previewingAlternative === null) {
        return {
          label: 'Primary Route',
          distance: this.selectedRoute.totalDistance,
          badWeatherPercent: this.getBadWeatherPercent(this.selectedRoute)
        };
      }
      
      const alternatives = this.getRouteAlternatives(this.selectedRoute);
      const alt = alternatives.find(a => a.alternativeIndex === this.previewingAlternative);
      if (!alt) return null;
      
      return {
        label: `Alternative #${alt.alternativeIndex}`,
        distance: alt.totalDistance,
        badWeatherPercent: alt.badWeatherPercent
      };
    },

    // Results helpers
    getRouteResults(route) {
      return this.routeResults[route.id] || [];
    },

    getRouteStatus(route) {
      const results = this.getRouteResults(route);
      if (results.length === 0) return null;

      if (results.some(r => r.maxSeverity === 'danger')) return 'danger';
      if (results.some(r => r.maxSeverity === 'warning')) return 'warning';
      return 'ok';
    },

    getRouteStatusLabel(route) {
      const status = this.getRouteStatus(route);
      switch (status) {
        case 'danger': return "Don't Ship";
        case 'warning': return 'Caution';
        case 'ok': return 'Clear';
        default: return '';
      }
    },

    /**
     * Recalculate severity for a result based on arrival hour temperature
     * This overrides the standard criteria evaluation with arrival-time specific check
     */
    recalculateArrivalSeverity(result) {
      // Silently handle missing data - these are expected conditions, not errors
      if (!result.arrivalInfo) {
        // No arrival info means this stop wasn't processed correctly - set neutral severity
        result.maxSeverity = result.maxSeverity || 'ok';
        return;
      }
      
      if (!result.days || result.days.length === 0) {
        // No weather data from API - keep existing severity or set to warning
        result.maxSeverity = result.maxSeverity || 'warning';
        return;
      }

      const arrivalDays = result.arrivalInfo.arrivalDays || 0;
      const arrivalHour = result.arrivalInfo.arrivalHour;

      // Find the arrival day (index based on arrivalDays offset)
      const arrivalDay = result.days[arrivalDays];
      if (!arrivalDay) {
        // Arrival day beyond forecast range - use last available day
        const lastDay = result.days[result.days.length - 1];
        if (lastDay) {
          const temp = lastDay.temp ?? lastDay.tempmax;
          if (temp !== undefined) {
            result.arrivalTemp = Math.round(temp);
            result.maxSeverity = this.evaluateTempSeverity(temp);
          }
        }
        return;
      }
      
      if (!arrivalDay.hours || arrivalDay.hours.length === 0) {
        // No hourly data, fall back to daily check
        const temp = arrivalDay.temp ?? arrivalDay.tempmax;
        if (temp !== undefined) {
          result.arrivalTemp = Math.round(temp);
          result.maxSeverity = this.evaluateTempSeverity(temp);
        }
        return;
      }

      // Find the closest hour to arrival time
      const hourData = arrivalDay.hours.find(h => {
        const hourTime = parseInt(h.datetime?.split(':')[0] || '0');
        return hourTime === arrivalHour;
      });

      if (hourData) {
        const temp = hourData.temp;
        result.arrivalTemp = Math.round(temp);
        result.arrivalConditions = hourData.conditions;
        result.maxSeverity = this.evaluateTempSeverity(temp);
      } else {
        // Fallback to daily average temp
        const temp = arrivalDay.temp ?? arrivalDay.tempmax;
        if (temp !== undefined) {
          result.arrivalTemp = Math.round(temp);
          result.maxSeverity = this.evaluateTempSeverity(temp);
        }
      }
    },

    /**
     * Evaluate temperature against thresholds and return severity
     */
    evaluateTempSeverity(temp) {
      if (temp < this.coldThreshold) {
        return 'danger';
      }
      if (temp > this.heatThreshold) {
        return 'danger';
      }
      // Check for severe weather risk (would need severerisk from hourly data)
      // For now, just return OK if within temperature bounds
      return 'ok';
    },

    /**
     * Get the arrival hour temperature for display
     */
    getArrivalTemp(result) {
      if (result.arrivalTemp !== undefined) {
        return result.arrivalTemp;
      }
      // Fallback to daily max
      return this.getMaxTemp(result);
    },

    getMaxTemp(result) {
      if (!result.days) return '--';
      const max = Math.max(...result.days.map(d => d.tempmax || 0));
      return Math.round(max);
    },

    getStopLabel(result) {
      // Show coordinates with 2 decimal places
      if (result.resolvedLocation?.latitude && result.resolvedLocation?.longitude) {
        const lat = result.resolvedLocation.latitude.toFixed(2);
        const lon = result.resolvedLocation.longitude.toFixed(2);
        return `${lat}, ${lon}`;
      }
      // Fallback to location string if no resolved coordinates
      const loc = result.location || '';
      return loc.substring(0, 15);
    },

    getStopTooltip(result) {
      const parts = [result.name || result.location];
      if (result.arrivalInfo) {
        parts.push(`Arrival: ${result.arrivalInfo.arrivalTimeStr}`);
        parts.push(`Distance: ${this.formatDistance(result.arrivalInfo.distanceKm)}`);
      }
      parts.push(`Temp @Arrival: ${this.getArrivalTemp(result)}${this.tempUnit}`);
      if (result.arrivalConditions) {
        parts.push(`Conditions: ${result.arrivalConditions}`);
      }
      return parts.join('\n');
    },

    getDayTemp(result, date) {
      const day = result.days?.find(d => d.datetime === date);
      return day ? Math.round(day.tempmax) + '°' : '--';
    },

    getDayCellClass(result, date) {
      const day = result.days?.find(d => d.datetime === date);
      if (!day) return '';

      if (day.severity === 'danger') return 'table-danger';
      if (day.severity === 'warning') return 'table-warning';
      return 'table-success';
    },

    formatDistance(km) {
      return formatDist(km, this.unitGroup);
    },

    formatShortDate(datetime) {
      const d = new Date(datetime + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
    }
  }
};
</script>

<style scoped>
.multi-route-tracker {
  padding: 1rem;
}

.tracker-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #dee2e6;
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0d6efd 0%, #6610f2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.route-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
  flex-shrink: 0;
}

.stop-type-icon {
  width: 24px;
  text-align: center;
}

.stop-list {
  max-height: 200px;
  overflow-y: auto;
}

/* Route Results */
.route-result-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.route-result-header {
  cursor: pointer;
  transition: background-color 0.2s;
}

.route-result-header:hover {
  filter: brightness(0.95);
}

/* Temperature Timeline */
.temp-timeline {
  background: #f8f9fa;
}

.temp-stop {
  text-align: center;
  position: relative;
  padding-top: 12px;
}

.temp-stop .stop-marker {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin: 0 auto 4px;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.temp-stop.severity-ok .stop-marker { background: #198754; }
.temp-stop.severity-warning .stop-marker { background: #ffc107; }
.temp-stop.severity-danger .stop-marker { background: #dc3545; }

.temp-stop .stop-temp {
  font-size: 0.875rem;
  font-weight: 600;
}

.temp-stop .stop-time {
  font-size: 0.625rem;
  font-weight: 500;
}

.temp-stop .stop-label {
  font-size: 0.625rem;
  color: #6c757d;
  max-width: 60px;
  margin: 0 auto;
}

/* Connect stops with line */
.temp-timeline .d-flex::before {
  content: '';
  position: absolute;
  top: 17px;
  left: 20px;
  right: 20px;
  height: 2px;
  background: #dee2e6;
  z-index: 0;
}

.temp-timeline .d-flex {
  position: relative;
}

.temp-stop .stop-marker {
  position: relative;
  z-index: 1;
}

.summary-card {
  background: #f8f9fa;
}

.empty-state {
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #dee2e6;
}

/* Map Styles */
.route-map {
  background: #e9ecef;
}

.route-marker {
  background: transparent !important;
  border: none !important;
}

/* Route Card Styles */
.route-card {
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

.route-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

/* Route List Styles */
.route-list-item {
  transition: background-color 0.15s;
}

.route-list-item:hover {
  background-color: #f8f9fa;
}

/* Modal Styles */
.route-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.route-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.route-modal-wide {
  max-width: 800px;
}

.route-modal-header {
  flex-shrink: 0;
}

.route-modal-body {
  overflow-y: auto;
  flex: 1;
}

.route-modal-footer {
  flex-shrink: 0;
}

/* Route Options Bar */
.route-options-bar .btn-group {
  flex-wrap: wrap;
}

.route-options-bar .btn {
  white-space: nowrap;
}

.route-options-bar .btn.route-recommended {
  border-color: #198754 !important;
}

.route-options-bar .recommended-star {
  position: absolute;
  top: -8px;
  right: -4px;
  color: #ffc107;
  font-size: 0.875rem;
  text-shadow: 0 0 2px rgba(0,0,0,0.5);
}

/* Alternatives Section */
.alternatives-section .alternative-item:first-child {
  border-top: none !important;
}

.alternatives-section .alternative-item {
  border-color: rgba(0,0,0,0.1) !important;
}
</style>
